<template>
  <div v-if="periods" class="plan-period__wrap">
    <div class="plan__discount-block">
      <h3 class="plan-period__title text-left">
        {{ $t('select') }}:
      </h3>
      <template v-if="discount">
        <div class="discount">
          <div class="plan__discount">
            {{ $t('discount') }}
            <span>{{ discount }}%</span>
            {{ $t('discount2') }}
          </div>
          <div class="plan__discount_text">
            {{ $t('discountText') }}
          </div>
        </div>
      </template>
    </div>
    <div class="plan-period__items-wrap">
      <div v-for="(period, index) in periods" :key="index" class="plan-item">
        <PaymentDialogPeriodItem
          :key="index"
          @input="onInput"
          :selected="selectedComp"
          :plan="plan"
          :period="period"
        />
      </div>
    </div>
  </div>
</template>

<script>
import usePeriod from '@/compositions/accounting/usePeriod';
import PaymentDialogPeriodItem from '@/components/Payment/Periods/PaymentDialogPeriodItem';
import {
  computed, ref, watch, onMounted,
} from '@vue/composition-api';
import useAccount from '@/compositions/accounting/useAccount';
import useDiscount from '@/compositions/useDiscount';

export default {
  name: 'PaymentDialogPeriods',
  components: { PaymentDialogPeriodItem },
  props: {
    plan: {
      type: Object,
      required: true,
    },
  },
  setup(_, { emit }) {
    const { period } = useAccount();
    const { periods, fetch } = usePeriod();
    const { discountInfo, fetchDiscountInfo } = useDiscount();
    fetch();
    const selected = ref(null);
    const selectedComp = computed({
      get() {
        return selected.value || (period.value ? period.value : periods.value.find((p) => p.default));
      },
      set(val) {
        selected.value = val;
      },
    });

    function onInput(val) {
      selected.value = val;
    }

    const discount = computed(() => discountInfo.value[0]?.discount);

    onMounted(async () => {
      await fetchDiscountInfo();
    });

    watch(selectedComp, () => {
      emit('input', selectedComp.value);
    });

    return {
      periods,
      onInput,
      selectedComp,
      discount,
    };
  },
};
</script>

<style lang="scss" scoped>
.plan-item {
  display: inline-block;
  max-width: 18rem;
  vertical-align: top;
  margin-right: 1.625rem;
  &:last-child {
    margin-right: 0;
  }
}
.plan-period {
  &__wrap {
    margin-bottom: 40px;
  }
  &__items-wrap {
    display: inline-block;
  }
}

.plan {
  &__discount {
    text-align: end;
    font-size: 17px;
    line-height: 17px;
    display: inline-block;
    color: #212121;
    margin-bottom: 5px;
    @media screen and (max-width: 599px) {
      font-size: 15px;
      line-height: 15px;
      text-align: start;
    }
    @media screen and (max-width: 428px) {
      display: block;
    }
    span {
      color: #0A82D9;
    }
    &_text {
      color: #212121;
      font-size: 15px;
      font-weight: 300;
      line-height: 15px;
      text-align: end;
      @media screen and (max-width: 599px) {
        text-align: start;
      }
      @media screen and (max-width: 428px) {
        font-size: 14px;
        line-height: 14px;
      }
    }
  }
  &__discount-block {
    display: flex;
    justify-content: space-between;
    margin-bottom: 40px;
    @media screen and (max-width: 599px) {
      display: block;
    }
  }
}

@media #{map-get($display-breakpoints, 'lg-and-down')} {
  .plan-item {
    margin-right: 1.125rem;
    margin-bottom: 1.125rem;
    &:last-child {
      margin-right: 0;
    }
  }
}
@media #{map-get($display-breakpoints, 'md-and-down')} {
  .plan-period {
    &__wrap {
      text-align: center;
      margin-bottom: 1.5rem;
    }
    &__items-wrap {
      max-width: 40rem;
    }
    &__title {
      font-size: 1.75rem;
      line-height: 1.2;
    }
    &__text {
      font-size: 1rem;
      font-weight: 400;
      line-height: 1.53;
      color: #091A35;
      margin-bottom: 1.25rem;
    }
  }
  .plan-item:last-child {
    margin-right: 1.25rem;
  }
}
@media #{map-get($display-breakpoints, 'sm-and-down')} {
  .plan-item {
    max-width: 50%;
    margin-bottom: 1rem;
    &:nth-child(2n) {
      margin-right: 0;
    }
  }
}
@media #{map-get($display-breakpoints, 'xs-only')} {
  .plan-item {
    margin-bottom: 0.3125rem;
  }
}
@media screen and (max-width: 740px) {
  .plan-period {
    padding: 1.875rem 1.25rem;
    &__wrap {
      margin-bottom: 1.25rem;
    }
    &__items-wrap {
      display: block;
    }
    &__title {
      font-size: 1rem;
      line-height: 1.1;
      margin-bottom: 0.625rem;
    }
    &__text {
      font-size: 1rem;
      font-weight: 400;
      line-height: 1.53;
      color: #091A35;
      margin-bottom: 0.875rem;
    }
  }
  .plan-item {
    display: block;
    max-width: 100%;
    margin-right: 0;
    &:last-child {
      margin-right: 0;
    }
  }
}
</style>

<i18n lang="yml" locale="ru">
  select: Выберите платёжный период
  discountText: Скидка применяется только в первый месяц оплаты тарифа
  discount: Скидка
  discount2: 'на первую оплату тарифа благодаря реферальной ссылке'
</i18n>
<i18n lang="yml" locale="en">
  select: Please select payment period
  discountText: The discount is applied only in the first month of payment of the tariff
  discount: 'You received a'
  discount2: 'discount for the first tariff payment thanks to the referral link'
</i18n>
